import { find } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { isNothing } from 'maybeasy';
import { Error } from '../../../../ErrorHandling';
import { ParticipantResource } from '../../../../ParticipantsStore/Types';
import { GroupParticipantsResource } from '../../Group/GroupParticipantsStore/Types';
import { BulkGroupMembershipsResource } from '../../Participants/BulkActions/AddParticipantsToGroup/UserGroupMembershipsForm/Store/Types';

export interface Ready {
  kind: 'ready';
  groupParticipantsResource: GroupParticipantsResource;
  newParticipants: ReadonlyArray<ParticipantResource>;
}

export interface NewParticipant {
  kind: 'new-participant';
  participantId: string;
  groupParticipantsResource: GroupParticipantsResource;
  newParticipants: ReadonlyArray<ParticipantResource>;
}

export interface RemoveParticipant {
  kind: 'remove-participant';
  participantId: string;
  groupParticipantsResource: GroupParticipantsResource;
  newParticipants: ReadonlyArray<ParticipantResource>;
}

export interface Submitting {
  kind: 'submitting';
  groupParticipantsResource: GroupParticipantsResource;
  newParticipants: ReadonlyArray<ParticipantResource>;
}

export interface ParticipantsAdded {
  kind: 'participants-added';
  participantsAdded: number;
}

export const ready = (groupParticipantsResource: GroupParticipantsResource): Ready => ({
  kind: 'ready',
  groupParticipantsResource,
  newParticipants: [],
});

export const participantsAdded = (
  bulkGroupMembershipsResource: BulkGroupMembershipsResource,
): ParticipantsAdded => ({
  kind: 'participants-added',
  participantsAdded: bulkGroupMembershipsResource.payload.successCount,
});

export const addNewParticipant = (state: Ready, participantId: string): NewParticipant => {
  const { kind, ...previous } = state;
  return {
    kind: 'new-participant',
    ...previous,
    participantId,
  };
};

export const removeParticipant = (state: Ready, participantId: string): RemoveParticipant => {
  const { kind, ...previous } = state;
  return {
    kind: 'remove-participant',
    ...previous,
    participantId,
  };
};

export const submit = (state: Ready): Submitting => ({
  ...state,
  kind: 'submitting',
});

const notInParticipantList =
  (newParticipant: ParticipantResource) =>
  (participants: readonly ParticipantResource[]): boolean =>
    isNothing(
      find((participant) => participant.payload.id === newParticipant.payload.id, participants),
    );

export const readyWithNewParticipant = (
  state: NewParticipant,
  participant: ParticipantResource,
): Ready => {
  const { kind, newParticipants, ...previous } = state;

  const participants = when(notInParticipantList(participant), newParticipants)
    .map((participants) => [...participants, participant])
    .getOrElseValue([...state.newParticipants]);

  return {
    kind: 'ready',
    ...previous,
    newParticipants: participants,
  };
};

export const readyWithRemovedParticipant = (state: RemoveParticipant): Ready => {
  const { kind, newParticipants, ...previous } = state;
  const participants = newParticipants.filter(
    (participant) => participant.payload.id.toString() !== state.participantId,
  );

  return {
    kind: 'ready',
    ...previous,
    newParticipants: participants,
  };
};

export type ParticipantState = Ready | NewParticipant | RemoveParticipant | Submitting;

export type State =
  | Ready
  | Error
  | NewParticipant
  | RemoveParticipant
  | Submitting
  | ParticipantsAdded;
